import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { AlertTriangle, ArrowRight, Scale, Info } from "lucide-react";

const POINTS = [
  "Offers can change or end without notice. Always confirm with the business before visiting.",
  "We do our best to verify every deal, but we can't guarantee accuracy at all times.",
  "Some rewards require a purchase, an app download, or advance signup — always check the details.",
  "BirthdayDeaLight is an independent project. We are not affiliated with any brand listed here.",
  "By using this app, you agree that we are not liable for expired, modified, or unavailable offers.",
];

export default function DisclaimerPage() {
  const navigate = useNavigate();

  const handleContinue = () => {
    localStorage.setItem("has_seen_disclaimer", "true");
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-rose-50 to-orange-50 flex flex-col" data-testid="disclaimer-page">
      {/* Header icon */}
      <div className="flex justify-center pt-12 pb-2">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.35 }}
          className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-red-400 to-rose-500 shadow-xl shadow-red-500/25"
        >
          <AlertTriangle className="w-8 h-8 text-white" />
        </motion.div>
      </div>

      {/* Title */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="text-center px-6 pb-4"
      >
        <h1 className="text-xl sm:text-2xl font-bold text-slate-800 mb-1" data-testid="disclaimer-title">
          Before You Dive In
        </h1>
        <p className="text-sm text-red-500/80 font-medium">
          You might wanna pay attention to this one
        </p>
      </motion.div>

      {/* Disclaimer content */}
      <div className="flex-1 px-6 pb-6">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-white/80 backdrop-blur-sm rounded-2xl border border-red-200/60 shadow-sm p-5 space-y-4"
        >
          {POINTS.map((text, i) => (
            <div key={i} className="flex items-start gap-3" data-testid={`disclaimer-point-${i}`}>
              <div className="flex-shrink-0 mt-0.5">
                <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center">
                  <span className="text-[10px] font-bold text-red-500">{i + 1}</span>
                </div>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">{text}</p>
            </div>
          ))}

          {/* Legal note */}
          <div className="mt-4 pt-3 border-t border-red-100 flex items-start gap-2">
            <Scale className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-slate-400 leading-relaxed">
              This is an informational tool, not a contractual guarantee. Use at your own discretion.
            </p>
          </div>
        </motion.div>
      </div>

      {/* CTA */}
      <div className="px-6 pb-10 w-full max-w-md mx-auto">
        <button
          onClick={handleContinue}
          className="w-full py-4 rounded-full bg-gradient-to-r from-red-400 to-rose-500 text-white font-semibold shadow-lg shadow-rose-500/30 hover:shadow-rose-500/50 transition-all flex items-center justify-center gap-2"
          data-testid="disclaimer-continue-btn"
        >
          I Understand — Let's Go
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
